/**
 * <predictive-search data-url="/search/suggest" data-params="resources[type]=…">
 * wraps a search form and a [data-predictive-results] container. Typing
 * (≥ 2 characters, 250 ms debounce) fetches the server-rendered
 * `predictive-search` section, aborting stale requests, and shows it as a
 * listbox: ArrowDown/ArrowUp move through options, Escape closes and
 * returns focus, outside click / focus leaving closes. A live region
 * announces the result count. The form still submits normally.
 */
class PredictiveSearch extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    this.input = this.querySelector('input[type="search"]');
    this.results = this.querySelector('[data-predictive-results]');
    this.status = this.querySelector('[data-predictive-status]');
    if (!this.form || !this.input || !this.results) return;

    this.cache = new Map();
    this.input.setAttribute('role', 'combobox');
    this.input.setAttribute('aria-autocomplete', 'list');
    this.input.setAttribute('aria-expanded', 'false');
    this.input.setAttribute('aria-controls', this.results.id);
    this.input.setAttribute('aria-haspopup', 'listbox');

    this.input.addEventListener('input', () => {
      this.dismissed = false;
      window.clearTimeout(this.timer);
      this.timer = window.setTimeout(() => this.search(), 250);
    });
    this.input.addEventListener('focus', () => {
      // Re-show cached results on refocus, unless the user dismissed them.
      if (!this.dismissed && this.input.value.trim().length >= 2 && this.results.innerHTML.trim()) this.open();
    });
    this.addEventListener('keydown', (event) => this.onKeydown(event));
    this.addEventListener('focusout', (event) => {
      if (event.relatedTarget && !this.contains(event.relatedTarget)) this.close();
    });
    document.addEventListener('click', (event) => {
      if (!this.contains(event.target)) {
        this.dismissed = true;
        this.close();
      }
    });
    this.form.addEventListener('submit', (event) => {
      if (!this.input.value.trim()) event.preventDefault();
    });
  }

  async search() {
    const query = this.input.value.trim();
    if (query.length < 2) {
      this.close();
      return;
    }
    this.abortController?.abort();
    this.abortController = new AbortController();
    this.classList.add('is-loading');
    try {
      let html = this.cache.get(query);
      if (html === undefined) {
        html = await this.fetchResults(query, this.abortController.signal);
        this.cache.set(query, html);
      }
      if (this.input.value.trim() !== query) return;
      this.render(html);
    } catch (error) {
      if (error.name !== 'AbortError') this.close();
    } finally {
      this.classList.remove('is-loading');
    }
  }

  async fetchResults(query, signal) {
    const url = `${this.dataset.url}?q=${encodeURIComponent(query)}&${this.dataset.params}&section_id=predictive-search`;
    const response = await fetch(url, { signal, headers: { Accept: 'text/html' } });
    if (!response.ok) throw new Error(`Predictive search failed: ${response.status}`);
    const text = await response.text();
    const doc = new DOMParser().parseFromString(text, 'text/html');
    return doc.querySelector('#shopify-section-predictive-search')?.innerHTML ?? '';
  }

  render(html) {
    this.results.innerHTML = html;
    if (!html.trim()) {
      this.close();
      return;
    }
    const statusText = this.results.querySelector('[data-predictive-status-text]');
    if (this.status) this.status.textContent = statusText ? statusText.textContent.trim() : '';
    this.open();
  }

  open() {
    if (!this.results.hidden) return;
    this.results.hidden = false;
    this.input.setAttribute('aria-expanded', 'true');
    this.classList.add('is-open');
    document.dispatchEvent(new CustomEvent('predictive:open'));
    this.dispatchEvent(new CustomEvent('menu:open', { bubbles: true }));
  }

  close() {
    if (this.results.hidden) return;
    this.results.hidden = true;
    this.input.setAttribute('aria-expanded', 'false');
    this.classList.remove('is-open');
  }

  onKeydown(event) {
    if (this.results.hidden) return;
    const options = Array.from(this.results.querySelectorAll('[role="option"]'));
    const index = options.indexOf(document.activeElement);
    if (event.key === 'ArrowDown' && options.length) {
      event.preventDefault();
      (options[index + 1] || options[0]).focus();
    } else if (event.key === 'ArrowUp' && options.length) {
      event.preventDefault();
      if (index > 0) options[index - 1].focus();
      else this.input.focus();
    } else if (event.key === 'Escape') {
      event.preventDefault();
      this.dismissed = true;
      this.close();
      this.input.focus();
    }
  }
}

if (!customElements.get('predictive-search')) customElements.define('predictive-search', PredictiveSearch);
